// Top handle of the custom sheets (Accueil, Mon suivi, Réserver, Bibliothèque, Nutrition, Le studio, Séance, account sheets)
// and of the home notifications panel. It replaces the close cross: a centred BUTTON « Fermer » on a full-width grip strip that
// also takes a real swipe down. Gesture contract reused from the vendored UserMenu sheet (drag on y, close when offset > 80 px
// or speed > 500 px/s, otherwise spring back), with plain pointer events (mouse, pen, touch).
// The sheet moves with the CSS `translate` property, independent of entrance keyframes on `transform` held by fill-mode.
// Interruptible (passe interactions 13/10): one active pointer at a time (a second finger is ignored); a grab during a spring
// (return or exit) stops it and continues from the LIVE presented offset, never from 0; release hands the finger's velocity
// to a spring (existing Motion `animate`, no new library); the exit completes on the spring's own end, not on a timer, so a
// regrab cancels it. Pointer capture at pointer-down; a tap (no drag) closes once on pointer-up; the keyboard (Enter / Space,
// click detail 0) closes through the button. Reduced motion: the sheet does not follow the finger (the UserMenu disables its
// drag), but a swipe past the threshold still closes and the button still works.
import { useRef, type PointerEvent } from "react";
import { animate } from "motion/react";

const CLOSE_OFFSET = 80;
const CLOSE_SPEED = 500;
export const reduced = () => typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

/** Live presented offset and in-flight spring of each sheet (one entry per sheet element, shared by every handle on it). */
const live = new WeakMap<HTMLElement, { y: number; stop?: () => void }>();
export const presentedY = (sheet: HTMLElement) => {
  const v = live.get(sheet);
  if (v) return v.y;
  const t = getComputedStyle(sheet).translate.split(" ")[1];
  return t && t.endsWith("px") ? parseFloat(t) : 0;
};
export const setY = (sheet: HTMLElement, y: number) => {
  const v = live.get(sheet) ?? { y: 0 };
  v.y = y; live.set(sheet, v);
  sheet.style.translate = y ? `0 ${y}px` : "";
};
/** Stop the sheet's in-flight spring, if any, leaving it where it is on screen. */
export const stopSpring = (sheet: HTMLElement) => { const v = live.get(sheet); v?.stop?.(); if (v) v.stop = undefined; };
/** Spring the sheet from its live offset to `to`, starting at `velocity` px/s (Apple drawer response .3). */
export const springTo = (sheet: HTMLElement, to: number, velocity: number, done?: () => void) => {
  stopSpring(sheet);
  const from = presentedY(sheet);
  // The entry is registered before the spring starts, so a synchronous completion still finds its own token.
  const entry: { y: number; stop?: () => void } = { y: from };
  const token = () => controls?.stop();
  entry.stop = token;
  live.set(sheet, entry);
  // Critically damped (no overshoot: a bounce would lift the sheet off the bottom edge); velocity handed off from the finger.
  const controls: { stop: () => void } | undefined = animate(from, to, {
    type: "spring", velocity, bounce: 0, visualDuration: 0.3,
    onUpdate: y => setY(sheet, y),
    onComplete: () => { if (live.get(sheet)?.stop !== token) return; entry.stop = undefined; if (to === 0) live.delete(sheet); done?.(); },
  });
};

/** `onClose`: the sheet's own close (its exit animation, if any). `onSwiped`: called once the swipe has already slid the sheet
 *  out, so sheets with an exit animation do not replay it; defaults to `onClose`.
 *  Single motion owner (13/10): when `onDismiss` is given (CustomSheet), a swipe past the threshold hands the release velocity to
 *  it instead of sliding the sheet out here, and `onGrab` tells the owner a pointer took the sheet (a close in flight is cancelled
 *  where it stands). Without them (the bell Drawer) the former behaviour is unchanged. */
export function DrawerHandle({ onClose, onSwiped = onClose, onDismiss, onGrab }: { onClose: () => void; onSwiped?: () => void; onDismiss?: (velocity: number) => void; onGrab?: () => void }) {
  const drag = useRef<{ id: number; startY: number; base: number; y: number; t: number; v: number; sheet: HTMLElement | null; moved: boolean } | null>(null);

  const down = (e: PointerEvent<HTMLDivElement>) => {
    if (e.button !== 0) return;
    // Active-pointer guard: a second pointer while one drags is ignored (no jump to the new finger).
    if (drag.current) return;
    // Capture from the start so a fast drag that leaves the strip on its first move is not lost (QA Root 13/10). The capture
    // retargets a tap's click to the strip, so taps are closed in onPointerUp, not by the button's click.
    e.currentTarget.setPointerCapture(e.pointerId);
    const sheet = e.currentTarget.closest<HTMLElement>('[role="dialog"]');
    // Regrab mid-flight: stop the spring where it is and continue from the presented offset.
    let base = 0;
    if (sheet) { stopSpring(sheet); base = presentedY(sheet); }
    onGrab?.();
    drag.current = { id: e.pointerId, startY: e.clientY, base, y: base, t: e.timeStamp, v: 0, sheet, moved: false };
  };
  const move = (e: PointerEvent<HTMLDivElement>) => {
    const d = drag.current; if (!d || d.id !== e.pointerId) return;
    const dy = e.clientY - d.startY;
    if (Math.abs(dy) > 4) d.moved = true;
    const y = Math.max(0, d.base + dy);
    // Velocity is measured over the last move, so a quick flick counts even after a slow start.
    const now = e.timeStamp;
    d.v = now > d.t ? ((y - d.y) / (now - d.t)) * 1000 : d.v;
    d.y = y; d.t = now;
    if (d.sheet && !reduced()) setY(d.sheet, y);
  };
  const up = (e: PointerEvent<HTMLDivElement>) => {
    const d = drag.current; if (!d || d.id !== e.pointerId) return;
    drag.current = null;
    const sheet = d.sheet;
    // A tap (pointer, no drag) anywhere on the strip, handle included, closes here, once.
    if (!d.moved) { onClose(); return; }
    if (d.y > CLOSE_OFFSET || d.v > CLOSE_SPEED) {
      if (onDismiss) { onDismiss(Math.max(d.v, 0)); return; }
      if (!sheet || reduced()) { onSwiped(); return; }
      springTo(sheet, sheet.offsetHeight, Math.max(d.v, 0), onSwiped);
      return;
    }
    if (sheet && presentedY(sheet)) springTo(sheet, 0, d.v);
  };
  const cancel = (e: PointerEvent<HTMLDivElement>) => {
    const d = drag.current; if (!d || d.id !== e.pointerId) return;
    drag.current = null;
    if (d.sheet && presentedY(d.sheet)) { if (reduced()) setY(d.sheet, 0); else springTo(d.sheet, 0, 0); }
  };

  return <div className="cc-drawer-grip" onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerCancel={cancel}>
    {/* Pointer taps close in onPointerUp above; the button's own click only acts for the keyboard (Enter / Space: detail 0),
        so a tap never closes twice. */}
    <button type="button" className="cc-drawer-handle" aria-label="Fermer" onClick={e => { if (e.detail === 0) onClose(); }}>
      <span aria-hidden="true" />
    </button>
  </div>;
}
